'use client';

import { useMemo, useState, useCallback } from "react";
import style from "./DegreeProgramListDetails.module.scss";
import ProgramCard2 from "@/revamp/molecules/ProgramCard2";
import UnivoCarousel, {
  useUnivoCarousel,
} from "@/components/common/UnivoCarousel";
import type { UnivoCarouselSettings } from "@/components/common/UnivoCarousel/types";
import { programMap } from "../utils";

import { useModalDispatch } from "@/context/ModalContext";
import { IModalActions, ModalTypes } from "@/types/modal";
import popupData from "@/data/popup-data.json";
import cn from "clsx";
import UnivoImage from "@/components/common/UnivoImage";
import { IMAGE_ASSETS } from "@/constants/images";
import { isIndiaCountry } from "@/helpers/countryCode";

export const hclTechPrograms = [
  "bba with specialization in data analytics",
  "bca with specialization in software engineering",
  "mca with specialization in cyber security",
  "mca with specialization in software engineering (online mca)",
  "mca with specialization in software engineering",
  "bca with specialization in data engineering"
];


export const tcsTechPrograms = [
  "mca with specialization in machine learning & artificial intelligence",
  "bca with specialization in data analytics",
  "bca with specialization in cloud and security"
];

export const kpmgTechPrograms = [
  "bba with a professional certificate in business analytics",
  "bca with a professional certificate in applied data engineering",
  "bba with professional certificate in business analytics",
  "bca with professional certificate in applied data engineering"
]

export const paytmTechPrograms1 = [
  "bca with specialization in financial technology and ai"
]

export const paytmTechPrograms2 = [
  "mca with specialization in financial technology and ai"
]

export const mbaCareerRestartPathway = [
  "mba with career restart pathway",
]

export const medaversityPrograms = [
  "mba in hospital and healthcare management"
]

export const ricsPage = [
  "mba in construction project management (cpm)"
]

export const pmiProgram = [
  "mba with pmi® certification pathways",
  "bba with pmi® certification pathways",
  "bca with pmi® certification pathways",
  "mca with pmi® certification pathways"
]


// Helper function to extract numeric value from FeesInInr
const getInrValue = (fee) => {
  if (!fee?.FeesInInr) return -Infinity; // Put nulls at the end
  return parseInt(fee.FeesInInr.replace(/[₹,*\s]/g, ''), 10);
};

const getDurationInMonths = (duration) => {
  if (!duration) return "";

  if (duration.includes("*")) {
    let years = duration.split("*")[0];
    years = years ? Number(years) : 0;
    return `${years * 12} months`;
  }

  return `${parseInt(duration?.split(" ")[0]) * 12} months`;
}

const getDisplayedFees = (fees) => {
  if (!Array.isArray(fees) || fees?.length === 0) return {
    FeesInInr: '',
    FeesInUSD: ''
  }

  // Sort in descending order (copy to avoid mutating the source FeesOptions array)
  const sortedFeeData = [...fees].sort((a, b) => getInrValue(b) - getInrValue(a));
  const feesBlock = sortedFeeData[0];
  return {
    FeesInInr: feesBlock?.FeesInInr,
    FeesInUSD: feesBlock?.FeesInUSD
  }
}

const getCertificationFee = (fees, country, programType) => {
  const showInr = isIndiaCountry(country);
  if (programType === "SOAI") {
    if (showInr) {
      return fees[0]?.feesInINR
    }
    return fees[0]?.feesInUSD;
  }
  if (!fees) return "";
  let match = "";
  if (showInr) {
    const feeInr = fees[4]?.Heading;
    match = feeInr.match(/₹(?:<del>[\d,\s]+<\/del>\s*)?₹([\d,]+)/);
  } else {
    const feeUSD = fees[4]?.HeadingUSD;
    match = feeUSD.match(/(?:₹|USD)\s*(?:<del>[\d,\s]+<\/del>\s*)?(?:₹|USD)\s*([\d,]+)/
    );
  }

  return match ? (showInr ? 'INR ' : '$') + match[1] : "";
}

const getCertificateDuration = (fees) => {
  if (!fees) return "";
  return fees[1]?.Subheading;
}

const checkIndustryAligned = (programName) => {
  if (hclTechPrograms.includes(programName?.toLowerCase())) {
    return "HCL";
  } else if (tcsTechPrograms.includes(programName?.toLowerCase())) {
    return "TCS";
  } else if (kpmgTechPrograms.includes(programName?.toLowerCase())) {
    return "KPMG";
  } else if (paytmTechPrograms1.includes(programName?.toLowerCase()) || paytmTechPrograms2.includes(programName?.toLowerCase())) {
    return "PAYTM";
  } else if (mbaCareerRestartPathway.includes(programName?.toLowerCase())) {
    return "WOMEN";
  } else if (medaversityPrograms.includes(programName?.toLowerCase())) {
    return "MEDAVERSITY";
  } else if (ricsPage.includes(programName?.toLowerCase())) {
    return "RICS";
  } else if (pmiProgram.includes(programName?.toLowerCase())) {
    return "PMI";
  } else {
    return "";
  }
}



const programCardDetailList = (programs) => {
  const result = programs?.map((d, index) => {
    const basicDetails = { ...d?.attributes?.BasicDetails };
    basicDetails["HeadingMobile"] = programMap[basicDetails?.Heading];
    const fees = getDisplayedFees(d?.attributes?.Fees?.FeesOptions || []);
    const duration = getDurationInMonths(d?.duration);
    const domain = d?.domain;
    const industryAligned = checkIndustryAligned(programMap[basicDetails?.Heading]);

    return {
      basicDetails,
      fees,
      duration,
      domain,
      industryAligned,
      discountedFeesInr: d?.discountedFeesInr,
    }
  });

  return result;
}

const certificationCardDetailList = (certifications, country) => {
  const result = certifications?.map((d, index) => {
    const basicDetails = { ...d?.attributes?.BasicDetails, ProgramType: d?.programType };
    const fees = getCertificationFee(basicDetails?.Fees, country, d?.programType);
    const duration = getCertificateDuration(basicDetails?.Fees);

    return {
      basicDetails,
      fees,
      duration
    }
  });

  return result;
}


export default function ProgramListing({
  programs,
  country,
  className = "",
  isCertificationList = false,
  isSchoolOfAIList = false,
  isAffiliatePage = false,
  isProgramListPage = false,
  corporateOffer = undefined,
  amsId = "",
}) {
  const modalDispatch = useModalDispatch();
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const { ref: sliderRef, next: handleNext, prev: handlePrev } =
    useUnivoCarousel();

  const handleSlideChange = useCallback(
    (swiper: { isBeginning: boolean; isEnd: boolean }) => {
      setIsAtStart(swiper.isBeginning);
      setIsAtEnd(swiper.isEnd);
    },
    []
  );

  const carouselSettings = useMemo<UnivoCarouselSettings>(
    () => ({
      pagination: {
        status: false,
        className: "program_detail_carousel_pagination text-center my-2",
      },
      onSlideChange: handleSlideChange,
    }),
    [handleSlideChange]
  );

  function schoolOfAIList(programs) {
    return programs.map(program => {
      const attrs = program.attributes;

      const feeItem = attrs?.fees.find(p =>
        isIndiaCountry(country) ? p.currency === "INR" : p.currency === "USD"
      );


      return {
        basicDetails: {
          isSponsoredCertification: true,
          enabled: true,
          url_key: `${process.env.NEXT_PUBLIC_SCHOOL_OF_API}/certifications/${attrs.slug}`,
          ProgramType: null,
          Heading: attrs.title,
          YearsIcon: {
            enabled: true,
            alt: "Amity Online",
            title: "Hours",
            url: null,
            target: ["_self"],
            source: {
              data: []
            }
          },
          Image: {
            source: {
              data: [{
                attributes:
                {
                  url: `${process.env.NEXT_PUBLIC_SCHOOL_OF_API}/cms` + attrs.image.data?.attributes?.url
                }
              }]
            }
          },
          TrendingType: null
        },
        fees: feeItem?.saleAmount || '',
        duration: attrs.duration || null
      };
    });
  }

  const onClickHandler = useCallback((urlKey = "") => {
    const programItem = programs.find(program => {
      return program?.attributes?.BasicDetails?.url_key === urlKey
    });

    if (isAffiliatePage) {
      modalDispatch({
        type: IModalActions.OpenModal,
        data: {
          modalType: ModalTypes.ConnectModalV2,
          modalData: {
            heading: "Apply Now",
            submitBtnText: "Apply Now",
            associatedPrograms: [programItem],
            isAffiliatePage: {isAffiliatePage},
            isLandingPage: true,
            rfiCase: "REDIRECT_APPLICATION_JOURNEY"
          },
        },
      })
    } else {
      modalDispatch({
        type: IModalActions.OpenModal,
        data: {
          modalType: ModalTypes.LandingPageConnectModalTempleteOne,
          modalData: {
            ...{
              associatedPrograms: [programItem],
              popup: popupData,
            }, href: "#", enquiryNow: true,
            isProgramListPage: isProgramListPage,
          },
        },
      });
    }
  }, [programs, isAffiliatePage, modalDispatch, isProgramListPage]);
  const transformProgram = (item) => ({
    attributes: {
      BasicDetails: {
        id: item?.basicDetails?.PROGRAM_ID, 
        Heading: item?.basicDetails?.Heading,
        url_key: item?.basicDetails?.url_key,
        ProgramType: item?.basicDetails?.ProgramType,
        SpecializationText: item?.basicDetails?.SpecializationText,
        PROGRAM_ID: item?.basicDetails?.PROGRAM_ID,
        Brochure: item?.basicDetails?.Brochure,
        Custom10PgType: item?.basicDetails?.Custom10PgType,
      }
    }
  });
  const isProgramList = !isSchoolOfAIList && !isCertificationList;
  const cardList = useMemo(() => {
    const cardDetails = isSchoolOfAIList ? schoolOfAIList(programs) : isCertificationList ? certificationCardDetailList(programs, country) : programCardDetailList(programs);
    return cardDetails?.map((item, index) => {
      return <ProgramCard2
        key={item?.basicDetails?.url_key || item?.basicDetails?.PROGRAM_ID || index}
        {...item}
        associatedPrograms={[transformProgram(item)]}
        country={country}
        isProgramList={isProgramList}
        isCertificationCard={isCertificationList}
        isSchoolOfAIList={isSchoolOfAIList}
        id={`get-brochure${index}`}
        onClickHandler={onClickHandler}
        isAffiliatePage={isAffiliatePage}
        isProgramListPage={isProgramListPage}
        corporateOffer={corporateOffer}
        amsId={amsId}
        discountedFeesInr={item?.discountedFeesInr}
      />
    })
  }, [
    programs,
    country,
    isSchoolOfAIList,
    isCertificationList,
    isProgramList,
    isAffiliatePage,
    isProgramListPage,
    corporateOffer,
    amsId,
    onClickHandler,
  ]);

  return (
    <>
      <div className={cn(style.program_list_container, className)}>
        {
          !(isAtStart && isAtEnd) && (
            <button
              className={cn(style.button, style.arrow_right)}
              onClick={handlePrev}
              disabled={isAtStart}
              id="degree-explore-more"
            >
              <UnivoImage src={IMAGE_ASSETS.chevronRight} alt="arrow right" width={24} height={24} sizes="24px" loading="lazy" />
            </button>
          )
        }

        <UnivoCarousel
          children={cardList}
          ref={sliderRef}
          preset="programs"
          settings={carouselSettings}
          id="degree-card"
          className={style.degreeProgramCarousel}
          isSmoothBehaviour={true}
        />
        {
          !(isAtEnd && isAtStart) && (
            <button
              className={cn(style.button, style.arrow_left)}
              onClick={handleNext}
              disabled={isAtEnd}
            >
              <UnivoImage src={IMAGE_ASSETS.chevronLeft} alt="arrow left" width={24} height={24} sizes="24px" loading="lazy" />
            </button>
          )
        }

      </div>
    </>
  )
}
